import React, { useState, type ReactNode } from "react";
import { Tabs } from "antd";
import ProtocolTable from "./ProtocolTable";
import { ProtocolsHeaderActions } from "./ProtocolsHeaderActions";
import { UnarchiveButton } from "../../shared/layout/UnarchiveButton";
import { useTranslation } from "react-i18next";
import { useProtocolsRouter } from "../../shared/custom-hooks/useProtocolsRouter";
import NewFiltersPanel, { type FiltersState } from "./NewFiltersPanel";
import CommonNewFiltersPanel from "./CommonNewFiltersPanel";
import { useFeatureFlags } from "../../context/LDFlagsContext";
import { formatTranslation } from "../../shared/utils/translationUtils";

interface ProtocolTabsProps {
  protocols: any[];
  rawProtocols: any[];
  columns: any[];
  pagination: any;
  filters: any;
  handlePagination: (pagination: any) => Promise<void>;
  handleRowClick: (protocolId: string) => void;
  updateFilter: (key: string, value: string) => void;
  handleUnarchive: (
    protocolId: string,
    orgId: string,
    workspaceId: string
  ) => Promise<void>;
  permissions: any;
  isLoading: boolean;
  isTemplatesTabEnabled: boolean;
  advancedFilters: Partial<FiltersState> | null;
  summaryInsights?: ReactNode;
}

const ProtocolTabs: React.FC<ProtocolTabsProps> = ({
  protocols,
  rawProtocols,
  columns,
  pagination,
  filters,
  handlePagination,
  handleRowClick,
  updateFilter,
  handleUnarchive,
  isLoading,
  isTemplatesTabEnabled,
  advancedFilters,
  summaryInsights,
}) => {
  const { t } = useTranslation();
  const { workspaceId, orgId } = useProtocolsRouter();

  const { enableNewFilter } = useFeatureFlags() as {
    enableNewFilter?: boolean;
  };

  const [isNewFilterOpen, setIsNewFilterOpen] = useState(false);

  const modifiedColumns = columns.map((col: any) => {
    const {
      filters: columnFilters,
      filteredValue,
      defaultFilteredValue,
      filterDropdown,
      filterIcon,
      onFilter,
      filterMultiple,
      filterSearch,
      ...columnWithoutFiltering
    } = col;

    if (col.key === "actions") {
      return {
        ...columnWithoutFiltering,
        render: (text: any, record: any) => {
          if (filters?.tab === "archived") {
            return (
              <UnarchiveButton
                onClick={async () => {
                  await handleUnarchive(
                    record.id,
                    orgId ?? "",
                    workspaceId ?? ""
                  );
                }}
              >
                {t("protocols.unarchive")}
              </UnarchiveButton>
            );
          }

          return col.render(text, record, record.status, handleUnarchive);
        },
      };
    }

    return columnWithoutFiltering;
  });

  const tabItems = [
    {
      key: "active",
      label: formatTranslation(t("protocols.tabs.active")),
      children: (
        <>
          {summaryInsights}
          <ProtocolTable
            protocols={protocols}
            columns={modifiedColumns}
            pagination={pagination}
            filters={filters}
            handlePagination={handlePagination}
            handleRowClick={handleRowClick}
            isLoading={isLoading}
          />
        </>
      ),
    },
    {
      key: "archived",
      label: formatTranslation(t("protocols.tabsLabel.archived")),
      children: (
        <ProtocolTable
          protocols={protocols}
          columns={modifiedColumns.filter((col: any) => col.key !== "actions")}
          pagination={pagination}
          filters={filters}
          handlePagination={handlePagination}
          handleRowClick={handleRowClick}
          isLoading={isLoading}
        />
      ),
    },
  ];

  if (isTemplatesTabEnabled) {
    tabItems.push({
      key: "templates",
      label: formatTranslation(t("protocols.tabs.templates")),
      children: (
        <ProtocolTable
          protocols={protocols}
          columns={modifiedColumns.filter((col: any) => col.key !== "actions")}
          pagination={pagination}
          filters={filters}
          handlePagination={handlePagination}
          handleRowClick={handleRowClick}
          isLoading={isLoading}
        />
      ),
    });
  }

  const activeTabKey =
    isTemplatesTabEnabled || filters.tab !== "templates"
      ? filters.tab
      : "active";

  return (
    <>
      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => {
          updateFilter("tab", key);
        }}
        items={tabItems}
        tabBarExtraContent={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ProtocolsHeaderActions
              onSearch={(value: string) => {
                updateFilter("searchText", value);
              }}
              onFilter={(value: string) => {
                updateFilter("status", value);
              }}
              searchValue={filters?.searchText ?? ""}
              filterValue={filters?.status ?? ""}
              activeTab={filters.tab}
              enableNewFilter={enableNewFilter}
              onOpenNewFilter={() => {
                setIsNewFilterOpen(true);
              }}
            />
          </div>
        }
      />

      {enableNewFilter && filters.tab === "active" && (
        <NewFiltersPanel
          key={filters.tab}
          open={isNewFilterOpen}
          onClose={() => {
            setIsNewFilterOpen(false);
          }}
          data={rawProtocols}
          initialFilters={advancedFilters ?? undefined}
          onApply={(filters) => {
            updateFilter("advancedFilters", JSON.stringify(filters));
            setIsNewFilterOpen(false);
          }}
          onCancel={() => {
            setIsNewFilterOpen(false);
          }}
        />
      )}

      {enableNewFilter &&
        (filters.tab === "archived" || filters.tab === "templates") && (
          <CommonNewFiltersPanel
            key={filters.tab}
            open={isNewFilterOpen}
            activeTab={filters.tab}
            onClose={() => {
              setIsNewFilterOpen(false);
            }}
            data={rawProtocols}
            initialFilters={advancedFilters ?? undefined}
            onApply={(filters) => {
              updateFilter("advancedFilters", JSON.stringify(filters));
              setIsNewFilterOpen(false);
            }}
            onCancel={() => {
              setIsNewFilterOpen(false);
            }}
          />
        )}
    </>
  );
};

export default ProtocolTabs;
